/**
 * The visitor's cookie consent on every page: shows the banner where opt-in
 * is required and no choice is saved, opens Cookie settings, saves a choice
 * and starts the tools it allows. The server sends the region, the saved
 * choice and Global Privacy Control with the first HTML, so the banner never
 * flashes and the page hydrates as rendered.
 *
 * Aliases: cookie consent provider, useConsent, cookie banner, cookie settings
 */

import { useMutation } from "@tanstack/react-query";
import { useRouterState } from "@tanstack/react-router";
import {
  createContext,
  type ReactNode,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { toast } from "sonner";

import { CookieBanner } from "~/components/consent/cookie-banner";
import { CookieSettingsDialog } from "~/components/consent/cookie-settings-dialog";
import { saveConsentChoice } from "~/lib/functions/consent";
import {
  deleteVendorCookies,
  syncConsentedTools,
  withdrawConsentedTools,
} from "~/lib/monitoring/consent-tools";
import {
  allowedConsentGroups,
  type ConsentGroups,
  type ConsentState,
  isEmbedPath,
  NO_CONSENT,
  withdrawsConsent,
} from "~/lib/shared/consent";

export interface ConsentContextValue {
  /** The tool groups that may run on this page. */
  readonly allowed: ConsentGroups;
  readonly openSettings: () => void;
}

const ConsentContext = createContext<ConsentContextValue | null>(null);

/** Layouts whose sidebar holds the account menu in the bottom-left corner. */
const SIDEBAR_LAYOUTS: ReadonlySet<string> = new Set(["/_user", "/admin"]);

export function ConsentProvider({
  state,
  children,
}: {
  readonly state: ConsentState;
  readonly children: ReactNode;
}): React.ReactElement {
  const embedded = useRouterState({
    select: (routerState) => isEmbedPath(routerState.location.pathname),
  });
  const besideSidebar = useRouterState({
    select: (routerState) =>
      routerState.matches.some((match) => SIDEBAR_LAYOUTS.has(match.routeId)),
  });
  const [choice, setChoice] = useState(state.choice);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [status, setStatus] = useState("");

  const allowed = useMemo(
    () => (embedded ? NO_CONSENT : allowedConsentGroups({ ...state, choice })),
    [embedded, state, choice],
  );

  useEffect(() => {
    syncConsentedTools(allowed);
  }, [allowed]);

  const save = useMutation({
    mutationFn: (groups: ConsentGroups) => saveConsentChoice({ data: groups }),
    meta: { surface: "cookie_consent" },
    onSuccess: (saved) => {
      deleteVendorCookies(saved);
      if (withdrawsConsent(allowed, saved)) {
        withdrawConsentedTools(saved);
        return;
      }
      setChoice(saved);
      setSettingsOpen(false);
      setStatus("Cookie choices saved.");
    },
    onError: () => {
      toast.error("Your cookie choices were not saved. Please try again.");
    },
  });

  const value = useMemo(
    (): ConsentContextValue => ({
      allowed,
      openSettings: () => setSettingsOpen(true),
    }),
    [allowed],
  );

  return (
    <ConsentContext.Provider value={value}>
      {!embedded && state.optInRegion && choice === null && (
        <CookieBanner
          corner={besideSidebar ? "right" : "left"}
          pending={save.isPending}
          onChoose={save.mutate}
          onManage={() => setSettingsOpen(true)}
        />
      )}
      {children}
      <CookieSettingsDialog
        open={settingsOpen}
        onOpenChange={setSettingsOpen}
        allowed={allowed}
        pending={save.isPending}
        onSave={save.mutate}
      />
      <p role="status" className="sr-only">
        {status}
      </p>
    </ConsentContext.Provider>
  );
}

export function useConsent(): ConsentContextValue {
  const context = useContext(ConsentContext);
  if (!context) {
    throw new Error("useConsent needs a ConsentProvider above it.");
  }
  return context;
}
